<script setup lang="ts">
import { computed } from 'vue';
import type { FieldSchema } from '../../../types';
import type { PasswordPolicyContext, PasswordRequirement } from '../../../helpers';
import {
  PASSWORD_POLICY_ERROR_KEYS,
  getPasswordRequirements,
  getPasswordStrength,
  getPasswordValidationErrors,
} from '../../../helpers';

const props = withDefaults(
  defineProps<{
    error?: string;
    feedback?: boolean;
    field?: FieldSchema;
    placeholder?: string;
    required?: boolean;
    validationRules?: string[];
  }>(),
  {
    placeholder: 'components.forms.placeholder.common',
  }
);

const model = defineModel<string>();

// Mirrors the backend `max:64` rule. bcrypt silently truncates at 72 bytes, so we cap input length
// to avoid two different long passwords hashing to the same value.
const PASSWORD_MAX_LENGTH = 64;

const passwordContext = computed<PasswordPolicyContext>(() => settings.value?.passwordContext ?? {});
const settings = computed(() => props?.field?.settings);
const showFeedback = computed(() => settings.value?.feedback || props.feedback);
const strength = computed(() => getPasswordStrength(String(model.value ?? ''), passwordContext.value));

const hasInput = computed(() => !!String(model.value ?? '').length);
const rules = computed<string[]>(() => settings.value?.validationRules || props.validationRules || []);
const requirements = computed(() => getPasswordRequirements(String(model.value ?? ''), rules.value));

// NOTE :: The length rule alone reads as the only requirement; append a strength item so users know length is
// necessary but not sufficient. It is met only once the password clears zxcvbn + the blocklist (strong).
const strengthRequirement = computed<PasswordRequirement>(() => ({
  key: 'components.formFields.password.requirements.rules.strongPassword',
  met: strength.value === 'strong',
}));

const allRequirements = computed(() => [...requirements.value, strengthRequirement.value]);

// Surfaces the active policy failure inside the meter once the length requirement is met: the zxcvbn
// "too easy to guess" message, a banned-word hit, or a personal-info hit. The length rule is already
// covered by the requirements checklist below, so it is excluded here. Clears once the meter is green
// (strong) or while the estimator is still loading.
const METER_ERROR_KEYS = [
  PASSWORD_POLICY_ERROR_KEYS.COMMON,
  PASSWORD_POLICY_ERROR_KEYS.BANNED_WORD,
  PASSWORD_POLICY_ERROR_KEYS.CONTEXT,
];

const guessabilityErrorKey = computed(() => {
  const errors = getPasswordValidationErrors(String(model.value ?? ''), passwordContext.value);

  return METER_ERROR_KEYS.find((key) => errors.includes(key)) ?? '';
});

const STRENGTH_META = {
  empty: { width: '0%', barClass: 'bg-transparent', label: '', labelClass: 'text-gray-400' },
  weak: {
    width: '33%',
    barClass: 'bg-red-500',
    label: 'components.formFields.password.requirements.weakLabel',
    labelClass: 'text-red-600',
  },
  medium: {
    width: '66%',
    barClass: 'bg-amber-500',
    label: 'components.formFields.password.requirements.mediumLabel',
    labelClass: 'text-amber-600',
  },
  strong: {
    width: '100%',
    barClass: 'bg-green-500',
    label: 'components.formFields.password.requirements.strongLabel',
    labelClass: 'text-green-600',
  },
} as const;

const strengthMeta = computed(() => STRENGTH_META[strength.value]);

function unfreezeInput(event: any) {
  // NOTE: When logging out the input can get frozen by Chrome's auto fill feature.
  // This is a Chrome issue. When reassigning the input value to itself the field works again.
  // eslint-disable-next-line no-self-assign
  event.target.value = event.target.value;
}
</script>

<template>
  <Password
    v-model="model"
    appendTo="self"
    toggleMask
    class="w-full"
    pt:hideicon="cursor-pointer"
    pt:panel="box-border w-full min-w-0"
    pt:showicon="cursor-pointer"
    :feedback="showFeedback"
    :input-props="{ maxlength: PASSWORD_MAX_LENGTH }"
    :invalid="!!error"
    :placeholder="$t(settings?.placeholder || placeholder)"
    :pt:input:root="['h-9 w-full text-sm', { 'af-field-error': !!error }]"
    :required="settings?.required || required"
    @click="unfreezeInput($event)"
  >
    <template #content>
      <div
        v-if="strength === 'empty'"
        class="text-xs text-gray-400"
      >
        {{ $t('components.formFields.password.requirements.promptLabel') }}
      </div>

      <div
        v-else
        class="mb-1"
      >
        <div class="h-2 w-full overflow-hidden rounded bg-gray-200">
          <div
            class="h-full rounded transition-all duration-300"
            :class="strengthMeta.barClass"
            :style="{ width: strengthMeta.width }"
          />
        </div>

        <p :class="['mt-1 text-xs font-medium', strengthMeta.labelClass]">
          {{ $t(strengthMeta.label) }}
        </p>

        <p
          v-if="guessabilityErrorKey"
          class="mt-1 text-xs text-red-600"
        >
          {{ $t(guessabilityErrorKey) }}
        </p>
      </div>
    </template>

    <template #footer>
      <Divider />

      <p class="mt-2 text-xs">{{ $t('components.formFields.password.requirements.title') }}:</p>

      <ul class="ml-0 mt-2 list-none space-y-1 pl-0">
        <li
          v-for="requirement in allRequirements"
          :key="requirement.key"
          :class="[
            'flex items-center gap-2 text-xs',
            requirement.met ? 'text-green-600' : hasInput ? 'text-red-600' : 'text-gray-400',
          ]"
        >
          <i :class="requirement.met ? 'pi pi-check-circle' : hasInput ? 'pi pi-times-circle' : 'pi pi-circle'" />
          <span>{{ $t(requirement.key) }}</span>
        </li>
      </ul>
    </template>
  </Password>
</template>
